import { CartAddEvent } from '@theme/events';

let ratesPromise = null;
let variantMapPromise = null;
let isReconciling = false;
let reconcileTimer = null;
let hasRenderedControls = false;

function loadJson(url) {
  return fetch(url, { credentials: 'omit' }).then((response) => (response.ok ? response.json() : {}));
}

function getAssetUrl(filename) {
  const script = document.querySelector('script[src*="fitting-service-cart.js"]');
  if (!script) return filename;
  return new URL(filename, script.src).toString();
}

function loadRates() {
  if (!ratesPromise) ratesPromise = loadJson(getAssetUrl('labour-rates.json')).catch(() => ({}));
  return ratesPromise;
}

function loadVariantMap() {
  if (!variantMapPromise) variantMapPromise = loadJson(getAssetUrl('fitting-service-variants.json')).catch(() => ({}));
  return variantMapPromise;
}

function priceKey(value) {
  return (Math.round(Number(value) * 100) / 100).toFixed(2);
}

function formatCurrency(value) {
  const rounded = Math.round(Number(value) * 100) / 100;
  const isWhole = rounded === Math.trunc(rounded);
  return `$${rounded.toLocaleString(undefined, {
    minimumFractionDigits: isWhole ? 0 : 2,
    maximumFractionDigits: 2,
  })}`;
}

function getProperty(item, key) {
  return item.properties && item.properties[key] ? item.properties[key].toString() : '';
}

function isFittingService(item) {
  return getProperty(item, '_fitting_service') === 'true';
}

function getCartUrl() {
  return `${Theme.routes.cart_url}.js`;
}

function getSectionIds() {
  return Array.from(document.querySelectorAll('cart-items-component'))
    .map((component) => component.dataset.sectionId)
    .filter(Boolean)
    .join(',');
}

function cartRequest(url, body) {
  return fetch(url, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      ...body,
      sections: getSectionIds(),
      sections_url: window.location.pathname,
    }),
  }).then((response) => response.json());
}

function dispatchCartUpdate(response) {
  document.dispatchEvent(
    new CartAddEvent({}, 'fitting-service-cart', {
      source: 'fitting-service-cart',
      itemCount: response.item_count,
      sections: response.sections,
    })
  );
}

function setCartFittingBusy(isBusy) {
  document.documentElement.classList.toggle('cart-fitting-is-loading', isBusy);

  document.querySelectorAll('.cart__checkout-button').forEach((button) => {
    if (!(button instanceof HTMLButtonElement)) return;

    if (isBusy) {
      if (!button.disabled) button.dataset.fittingEnabledBeforeCheck = 'true';
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
    } else {
      if (button.dataset.fittingEnabledBeforeCheck === 'true') button.disabled = false;
      delete button.dataset.fittingEnabledBeforeCheck;
      button.removeAttribute('aria-busy');
    }
  });

  document.querySelectorAll('.cart__ctas').forEach((ctas) => {
    let status = ctas.querySelector('.cart__fitting-status');
    if (!status) {
      status = document.createElement('small');
      status.className = 'cart__fitting-status';
      status.setAttribute('role', 'status');
      ctas.prepend(status);
    }
    status.textContent = isBusy ? 'Checking fitting discounts before checkout...' : '';
    status.hidden = !isBusy;
  });
}

function renderPendingControls() {
  if (hasRenderedControls) return;

  document.querySelectorAll('[data-cart-product-line] [data-fitting-cart-control]').forEach((control) => {
    control.hidden = false;
    control.innerHTML = `
      <div class="cart-items__fitting-toggle cart-items__fitting-toggle--loading">
        <span class="cart-items__fitting-copy">
          <span class="cart-items__fitting-title">Checking fitting options...</span>
          <span class="cart-items__fitting-subtitle">We'll confirm any multi-item fitting discount before checkout.</span>
        </span>
      </div>
    `;
  });
}

function findServiceForProduct(product, services) {
  const productGroupId = getProperty(product, '_fitting_group_id');
  return services.find((service) => {
    const serviceGroupId = getProperty(service, '_fitting_group_id');
    const serviceForKey = getProperty(service, '_fitting_for_key');
    return (productGroupId && serviceGroupId === productGroupId) || serviceForKey === product.key;
  });
}

function getTargetPrice(product, rate, fittedCount) {
  if (fittedCount > 1) {
    return Number(rate.price_with_other) || Number(rate.price_alone) || 0;
  }

  return Number(rate.price_alone) || 0;
}

async function fetchCart() {
  return fetch(getCartUrl(), { credentials: 'same-origin' }).then((response) => response.json());
}

async function addFitting(product, rate, variantMap, useCombinedRate) {
  const price = useCombinedRate ? Number(rate.price_with_other) || Number(rate.price_alone) || 0 : Number(rate.price_alone) || 0;
  const variantId = variantMap[priceKey(price)];
  if (!variantId) return;

  const groupId = getProperty(product, '_fitting_group_id') || product.key;
  const response = await cartRequest(Theme.routes.cart_add_url, {
    items: [
      {
        id: Number(variantId),
        quantity: product.quantity,
        properties: {
          'Fitting for': product.product_title,
          _fitting_service: 'true',
          _fitting_group_id: groupId,
          _fitting_for_key: product.key,
          _fitting_for_sku: product.sku || '',
          _fitting_mode: useCombinedRate ? 'with_other' : 'alone',
          _fitting_price_alone: priceKey(rate.price_alone),
          _fitting_price_with_other: priceKey(Number(rate.price_with_other) || Number(rate.price_alone)),
        },
      },
    ],
  });

  dispatchCartUpdate(response);
  scheduleReconcile();
}

async function removeFitting(service) {
  const response = await cartRequest(Theme.routes.cart_change_url, {
    id: service.key,
    quantity: 0,
  });

  dispatchCartUpdate(response);
  scheduleReconcile();
}

async function replaceFittingService(product, service, rate, targetPrice, targetMode, variantMap) {
  const targetVariantId = Number(variantMap[priceKey(targetPrice)]);
  if (!targetVariantId) return false;

  const currentVariantId = Number(service.variant_id);
  const currentMode = getProperty(service, '_fitting_mode');
  if (currentVariantId === targetVariantId && service.quantity === product.quantity && currentMode === targetMode) {
    return null;
  }

  await cartRequest(Theme.routes.cart_change_url, {
    id: service.key,
    quantity: 0,
  });

  return cartRequest(Theme.routes.cart_add_url, {
    items: [
      {
        id: targetVariantId,
        quantity: product.quantity,
        properties: {
          'Fitting for': product.product_title,
          _fitting_service: 'true',
          _fitting_group_id: getProperty(service, '_fitting_group_id') || getProperty(product, '_fitting_group_id') || product.key,
          _fitting_for_key: product.key,
          _fitting_for_sku: product.sku || '',
          _fitting_mode: targetMode,
          _fitting_price_alone: priceKey(rate.price_alone),
          _fitting_price_with_other: priceKey(Number(rate.price_with_other) || Number(rate.price_alone)),
        },
      },
    ],
  });
}

async function renderControls(cart, rates, variantMap) {
  const services = cart.items.filter(isFittingService);
  hasRenderedControls = true;

  document.querySelectorAll('[data-cart-product-line]').forEach((row) => {
    const sku = row.dataset.sku || '';
    const control = row.querySelector('[data-fitting-cart-control]');
    const product = cart.items.find((item) => item.key === row.dataset.key);
    const rate = rates[sku];

    if (!control || !product || !rate || Number(rate.price_alone) <= 0) return;

    const service = findServiceForProduct(product, services);
    const price = service ? service.final_line_price / Math.max(service.quantity, 1) / 100 : Number(rate.price_alone);
    const variantReady = Boolean(variantMap[priceKey(rate.price_alone)]);
    const hasMultiItemRate =
      service && getProperty(service, '_fitting_mode') === 'with_other' && priceKey(price) !== priceKey(rate.price_alone);
    const hasSameMultiItemRate =
      service && getProperty(service, '_fitting_mode') === 'with_other' && priceKey(price) === priceKey(rate.price_alone);
    const fittingSubtitle = hasMultiItemRate
      ? `Multi-item fitting rate applied. Standard fitting is ${formatCurrency(rate.price_alone)}.`
      : service
        ? hasSameMultiItemRate
          ? 'This item has the same fitting charge when installed with other products.'
          : 'Charged as an installation add-on.'
        : 'Add this installation service to your order.';

    control.hidden = false;
    control.innerHTML = `
      <label class="cart-items__fitting-toggle">
        <input type="checkbox" data-fitting-cart-checkbox ${service ? 'checked' : ''} ${variantReady ? '' : 'disabled'}>
        <span class="cart-items__fitting-copy">
          <span class="cart-items__fitting-title">${service ? 'Professional fitting added' : 'Add professional fitting'} - ${formatCurrency(price)}</span>
          <span class="cart-items__fitting-subtitle">${fittingSubtitle}</span>
        </span>
      </label>
      ${variantReady ? '' : '<small class="cart-items__fitting-message">Fitting service setup pending.</small>'}
    `;

    const checkbox = control.querySelector('[data-fitting-cart-checkbox]');
    checkbox?.addEventListener('change', async () => {
      checkbox.disabled = true;
      if (checkbox.checked) {
        await addFitting(product, rate, variantMap, services.length > 0);
      } else if (service) {
        await removeFitting(service);
      }
    });
  });
}

async function reconcileFittingServices() {
  if (isReconciling) return;
  isReconciling = true;
  setCartFittingBusy(true);
  renderPendingControls();

  try {
    const [cart, rates, variantMap] = await Promise.all([fetchCart(), loadRates(), loadVariantMap()]);
    const products = cart.items.filter((item) => !isFittingService(item) && rates[item.sku]);
    const services = cart.items.filter(isFittingService);
    const pairs = products
      .map((product) => ({ product, rate: rates[product.sku], service: findServiceForProduct(product, services) }))
      .filter((pair) => pair.service && Number(pair.rate.price_alone) > 0);

    let latestResponse = null;

    for (const service of services) {
      if (!pairs.some((pair) => pair.service.key === service.key)) {
        latestResponse = await cartRequest(Theme.routes.cart_change_url, { id: service.key, quantity: 0 });
      }
    }

    for (const pair of pairs) {
      const targetMode = pairs.length > 1 ? 'with_other' : 'alone';
      const targetPrice = getTargetPrice(pair.product, pair.rate, pairs.length);
      latestResponse =
        (await replaceFittingService(pair.product, pair.service, pair.rate, targetPrice, targetMode, variantMap)) ||
        latestResponse;
    }

    if (latestResponse) {
      dispatchCartUpdate(latestResponse);
      return;
    }

    await renderControls(cart, rates, variantMap);
    setCartFittingBusy(false);
  } catch (error) {
    console.error(error);
    setCartFittingBusy(false);
  } finally {
    isReconciling = false;
  }
}

function scheduleReconcile(delay = 25) {
  if (reconcileTimer) window.clearTimeout(reconcileTimer);
  reconcileTimer = window.setTimeout(reconcileFittingServices, delay);
}

document.addEventListener('DOMContentLoaded', () => {
  loadRates();
  loadVariantMap();
  scheduleReconcile(0);
});
document.addEventListener('cart:update', () => {
  hasRenderedControls = false;
  scheduleReconcile(0);
});
document.addEventListener('shopify:section:load', () => {
  hasRenderedControls = false;
  scheduleReconcile(0);
});
document.addEventListener('fitting:cart-drawer-open', () => {
  hasRenderedControls = false;
  scheduleReconcile(0);
});
